import CallModal from 'app/components/call/CallModal';
import CallModalMascot from 'app/components/call/CallModalMascot';
import ReloadButton from 'app/components/call/ReloadButton';
import TroubleshootingLink from 'app/components/call/TroubleshootingLink';
import BaseButton from 'app/components/common/BaseButton';
import Form from 'app/components/forms/Form';
import TextArea from 'app/components/forms/TextArea';
import FlexMarginGroup from 'app/components/layout/FlexMarginGroup';
import { useI18n } from 'app/components/localization';
import ButtonClassNameA, { ButtonA, ColorScheme } from 'app/components/presentation/buttons/ButtonClassNameA';
import { StackedLabel } from 'app/components/presentation/forms/Labels';
import { getLinkStyles } from 'app/components/presentation/link/styles';
import SvgError from 'app/components/svgs/mascot/Worried';
import { GetReportProblemModalControl, IsReportProblemModalOpen } from 'app/containers/call/ReportProblemModalContext';
import { BookingStateConsumer } from 'app/containers/rtc/BookingContext/BookingState';
import { GetDevices } from 'app/containers/rtc/DeviceManagerContext';
import { GetPeers, Peer } from 'app/containers/rtc/PeersContext';
import { useRTC, ISdpDumpWithId } from 'app/containers/rtc/Provider';
import { GetPeersPlaybackControl, PlaybackState } from 'app/containers/rtc/PeersPlaybackContext';
import { BookingState } from 'app/models/rtc/booking/constants';
import { notify as defaultNotify } from 'app/services/errorReporter';
import { useCss } from 'app/utils/css';
import { Map } from 'immutable';
import React, { memo, useState } from 'react';
import { IDeviceManagerState } from 'app/services/rtc/DeviceManager/DeviceManagerState';

const userReportErrorName = 'UserRtcProblemReport';

interface IProps {
  readonly bookingState?: BookingState;
  readonly devices?: IDeviceManagerState;
  readonly peers?: Map<string, Peer>;
  readonly playback?: PlaybackState;
}

interface IDeps {
  readonly close: () => void;
  readonly notify: (error: Error, metadata: any) => void;
}

// Time that must have elapsed since the lesson started before the form is enabled.
export const TEACHER_SUBMISSION_COUNTDOWN_MS = 30000;

export const ReportProblemModal = memo<IProps & IDeps>((props) => {
  const {
    bookingState,
    close,
    devices,
    notify,
    peers,
    playback,
  } = props;

  const rtc = useRTC();

  const [userFeedback, setUserFeedback] = useState('');
  const { t, tx } = useI18n();
  const css = useCss();

  const onSubmit = async () => {
    let sdps: ISdpDumpWithId[];

    try {
      sdps = (await rtc?.dumpAllSdps()) ?? [];
    } catch (error) {
      sdps = [];
    }

    notify(new Error(userReportErrorName), {
      bookingState,
      name: userReportErrorName,
      playback,
      userFeedback,
      // This is just to make the message in the console act as
      // an accurate preview of what's being sent to HB, without
      // any immutable structures, making sure `.toJSON` methods
      // return the right value, no memory leaks, etc.
      ...JSON.parse(JSON.stringify({
        devices,
        peers,
        sdps,
      })),
    });

    close();
  };

  const reloadLink = (
    <ReloadButton className={css('link', getLinkStyles())}>
      {t('rtc.report-problem.teacher.no-skype-explanation.reload')}
    </ReloadButton>
  );

  const troubleshootLink = (
    <TroubleshootingLink target="_blank">
      {t('rtc.report-problem.teacher.no-skype-explanation.troubleshoot')}
    </TroubleshootingLink>
  );

  return (
    <CallModal
      buttons={
        <div className={css('smallButtons', { fontSize: '14px' })}>
          <FlexMarginGroup wrap center>
            <ButtonA onClick={close} colorScheme={ColorScheme.Secondary}>
              {t('rtc.report-problem.teacher.cancel-button')}
            </ButtonA>

            <ButtonClassNameA>{(buttonClassNameA) => (
              <BaseButton
                className={buttonClassNameA}
                data-testid="report-problem-send"
                onClick={onSubmit}
                type="submit"
              >
                {t('rtc.report-problem.teacher.report-issue-button')}
              </BaseButton>
            )}
            </ButtonClassNameA>
          </FlexMarginGroup>
        </div>
      }
      onClose={close}
      title={t('rtc.report-problem.teacher.title')}
    >
      <Form onSubmit={onSubmit}>
        <div>
          <CallModalMascot>
            <SvgError width="100%" />
          </CallModalMascot>

          <p>{tx('rtc.report-problem.teacher.no-skype-explanation.p2', {
            reload: reloadLink,
            troubleshoot: troubleshootLink,
          })}</p>

          <div className={css('inputWrapper', { margin: '1em 2em 0', textAlign: 'left' })}>
            <StackedLabel
              label={t('rtc.report-problem.teacher.no-skype-textarea-message')}
              optional
            >
              <div
                className={css('textArea', {
                  display:        'flex',
                  flexDirection:  'column',
                  justifyContent: 'stretch',
                  alignItems:     'stretch',
                })}
              >
                <TextArea
                  autoFocus
                  autoResize
                  data-testid="report-problem-input"
                  immediate
                  minHeight="6em"
                  onChange={setUserFeedback}
                  value={userFeedback}
                />
              </div>
            </StackedLabel>
          </div>
        </div>
      </Form>
    </CallModal>
  );
});

const ReportProblemModalInjectDependencies = () => {
  return <IsReportProblemModalOpen>{(isOpen) => isOpen &&
    <GetReportProblemModalControl>{(control) => control &&
      <GetDevices>{(devices) =>
        <GetPeers>{(peers) =>
          <BookingStateConsumer>{(bookingState) =>
            <GetPeersPlaybackControl>{(playbackControl) =>
              <ReportProblemModal
                bookingState={bookingState}
                close={control.close}
                devices={devices}
                notify={defaultNotify}
                peers={peers}
                playback={playbackControl && playbackControl.state}
              />
            }</GetPeersPlaybackControl>
          }</BookingStateConsumer>
        }</GetPeers>
      }</GetDevices>
    }</GetReportProblemModalControl>
  }</IsReportProblemModalOpen>;
};

export default ReportProblemModalInjectDependencies;
